import React, { useState } from 'react';
import moment from 'moment-timezone';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { DialogBox } from '@mummssoftware/common-ui';
import { RecordVisitButtonsDiv } from '@mummssoftware/common-ui/DeleteVisitConfirmation';
import { useStyles as useButtonStyles } from '@mummssoftware/common-ui/VisitAttributeSelector';
import {
  handleTimeChange,
  minutesToTimerHhMmParts,
} from '@mummssoftware/utils/visitUtils';

import { TimerDiv, EditTimeDiv, TimerText } from '../../components';
import { useStyles } from '../Timer/style';
import defaults from '../../translations/en.json';

export type HhMm = [string, string];
export type DurationError = 'exceedTime' | 'ccExceedsDuration' | null;

const MAX_VISIT_MINS = 16 * 60;

export const toMinutes = ([hh, mm]: HhMm) =>
  (Number(hh) || 0) * 60 + (Number(mm) || 0);

/**
 * Pre-fill: time since the visit started when that is a valid duration (1 min to 16 h),
 * else the duration already saved on the visit (reopened visits), else empty.
 */
export const getInitialDuration = (
  startDate: string | null | undefined,
  now: moment.Moment,
  savedMins: number | null,
): HhMm => {
  const elapsed = startDate
    ? now.diff(
        moment.utc(startDate, ['MM/DD/YYYY HH:mm:ss', moment.ISO_8601]),
        'minutes',
      )
    : 0;
  if (elapsed >= 1 && elapsed <= MAX_VISIT_MINS) {
    return minutesToTimerHhMmParts(elapsed);
  }
  return savedMins ? minutesToTimerHhMmParts(savedMins) : ['', ''];
};

export const getDurationError = (
  durationMins: number,
  ccMins: number,
): DurationError => {
  if (durationMins < 1 || durationMins > MAX_VISIT_MINS) return 'exceedTime';
  if (ccMins > durationMins) return 'ccExceedsDuration';
  return null;
};

type HhMmFieldsProps = {
  id: string;
  label: string;
  value: HhMm;
  hasError: boolean;
  onChange: (value: HhMm) => void;
};

const HhMmFields = ({
  id,
  label,
  value,
  hasError,
  onChange,
}: HhMmFieldsProps) => {
  const classes = useStyles();
  const [hour, minute] = value;
  const inputProps = {
    classes: {
      input: classes.input,
      root: hasError ? classes.outlinedError : classes.outlined,
    },
  };
  const labelProps = { shrink: true, classes: { root: classes.labelInput } };
  const handleHourChange = handleTimeChange((event) =>
    onChange([event.target.value, minute]),
  );
  const handleMinChange = handleTimeChange((event) =>
    onChange([hour, event.target.value]),
  );

  return (
    <EditTimeDiv>
      <FormHelperText style={{ paddingBottom: '5px' }}>{label}</FormHelperText>
      <TimerDiv edit>
        <TextField
          label="HH"
          id={`${id}-hour`}
          variant="outlined"
          value={hour}
          InputProps={inputProps}
          onChange={handleHourChange}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ maxLength: 2 }}
          InputLabelProps={labelProps}
        />
        <TimerText>:</TimerText>
        <TextField
          label="MM"
          id={`${id}-min`}
          variant="outlined"
          value={minute}
          InputProps={inputProps}
          onChange={handleMinChange}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ maxLength: 2 }}
          InputLabelProps={labelProps}
        />
      </TimerDiv>
    </EditTimeDiv>
  );
};

type DurationPromptProps = {
  initialDuration: HhMm;
  /** CC hours already on the visit; null hides the CC field. */
  initialCC: HhMm | null;
  onCancel: () => void;
  onSubmit: (durationMins: number, ccMins: number) => void;
};

/** Asks for the visit duration (and lets the user fix CC hours) when Submit Visit is clicked. */
export const DurationPrompt = ({
  initialDuration,
  initialCC,
  onCancel,
  onSubmit,
}: DurationPromptProps) => {
  const buttonClasses = useButtonStyles();
  const [duration, setDuration] = useState<HhMm>(initialDuration);
  const [cc, setCC] = useState<HhMm | null>(initialCC);
  const [error, setError] = useState<DurationError>(null);

  const handleSubmit = () => {
    const durationMins = toMinutes(duration);
    const ccMins = cc ? toMinutes(cc) : 0;
    const nextError = getDurationError(durationMins, ccMins);
    setError(nextError);
    if (!nextError) {
      onSubmit(durationMins, ccMins);
    }
  };

  return (
    <DialogBox
      title={defaults.durationPromptTitle}
      disableBackdropClick
      open
      handleClick={onCancel}
      noIcon
    >
      <HhMmFields
        id="duration-prompt"
        label={defaults.editTime}
        value={duration}
        hasError={error !== null}
        onChange={setDuration}
      />
      {cc && (
        <HhMmFields
          id="duration-prompt-cc"
          label={defaults.editCC}
          value={cc}
          hasError={error === 'ccExceedsDuration'}
          onChange={setCC}
        />
      )}
      {error && <Typography color="error">{defaults[error]}</Typography>}
      <RecordVisitButtonsDiv>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={onCancel}
          classes={{ root: buttonClasses.button }}
        >
          {defaults.cancel}
        </Button>
        <Button
          id="duration-prompt-submit"
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          classes={{ root: buttonClasses.button }}
        >
          {defaults.submitVisit}
        </Button>
      </RecordVisitButtonsDiv>
    </DialogBox>
  );
};
